/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChangeDetectionStrategy, Component, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';
import { StepperStepConfig } from '@vsolv/vectors-ui/stepper';
import { SecurityService } from '@wsphere/staff/web';
import { Claim } from '@wsphere/warranties/domain';
import { combineLatest, map, Subject, switchMap, takeUntil } from 'rxjs';
import { ClaimService } from '../../services';

enum pages {
  DETAILS = 1,
  CURE = 2,
  ADJUDICATE = 3,
  RESOLVE = 4,
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-claim-item-dialog',
  templateUrl: './claim-item.dialog.html',
  standalone: false,
})
export class ClaimItemDialog implements OnDestroy {
  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private claimSvc: ClaimService,
    private securitySvc: SecurityService
  ) {
    router.events.pipe(takeUntil(this.destroy$)).subscribe(() => this.setPage());
  }

  @ViewChild('detailsDialog') detailsDialog?: DialogComponent;

  readonly item$ = this.route.data.pipe(map(data => data['item'] as Claim.ClaimItem.Model));
  readonly claim$ = this.claimSvc.getClaim();

  draft = Claim.ClaimItem.Status.DRAFT;

  readonly isCustomerPortal$ = this.route.data.pipe(map(data => data['isCustomerPortal'] as boolean));

  canAdjudicateClaimItem$ = combineLatest([this.claim$, this.isCustomerPortal$]).pipe(
    switchMap(async ([claim, isCustomerPortal]) => {
      if (isCustomerPortal) return false;

      const permissionKey = claim?.warranty?.distributor?.permissionKey
        ? [claim?.warranty?.distributor?.permissionKey]
        : null;
      return await this.securitySvc.hasAccess('clm_AdjudicateClaimItem', permissionKey);
    })
  );

  steps$ = combineLatest([this.item$, this.claim$, this.canAdjudicateClaimItem$, this.isCustomerPortal$]).pipe(
    map(([item, claim, canAdjudicateItem, isCustomerPortal]) => {
      const steps: StepperStepConfig[] = [];

      steps.push({ title: 'Details', subtitle: "Review the item's details", allowJumpToStep: true, routerLink: '' });
      steps.push({ title: 'Cure', subtitle: "Review the item's cure", allowJumpToStep: true, routerLink: 'cure' });

      if (!isCustomerPortal) {
        const ready = !!item && Claim.ClaimItem.readyForAdjudication(claim, item);
        steps.push({
          title: 'Adjudicate',
          subtitle: 'Approve or reject this item',
          allowJumpToStep: canAdjudicateItem ? ready : false,
          routerLink: canAdjudicateItem
            ? !ready
              ? undefined
              : item?.adjudicationReason
                ? 'adjudicate'
                : 'manage-adjudication'
            : undefined,
        });
      }

      steps.push({
        title: 'Resolve',
        subtitle: 'Confirm the issue was resolved',
        allowJumpToStep: !!item?.adjudicationReason && item?.status !== Claim.ClaimItem.Status.DRAFT,
        routerLink: !item?.adjudicationReason || item?.status === Claim.ClaimItem.Status.DRAFT ? undefined : 'resolve',
      });

      return steps;
    })
  );

  page = 1;
  pages = pages;

  menuOpen = false;

  setPage() {
    switch (this.route.snapshot.children?.[0].routeConfig?.path) {
      case 'cure':
        this.page = pages.CURE;
        break;
      case 'adjudicate':
        this.page = pages.ADJUDICATE;
        break;
      case 'resolve':
        this.page = pages.RESOLVE;
        break;
      default:
        this.page = pages.DETAILS;
    }
  }

  checkIfIncluded(claim: Claim.Model, item: Claim.ClaimItem.Model) {
    if (!claim || !item?.coverageId) return false;

    return Claim.ClaimItem.included(claim, item.coverageId);
  }

  switchItems(itemId: string) {
    this.menuOpen = false;

    let page = '';
    switch (this.page) {
      case pages.CURE:
        page = 'cure';
        break;
      case pages.ADJUDICATE:
        page = 'adjudicate';
        break;
      case pages.RESOLVE:
        page = 'resolve';
        break;
    }

    this.navigateTo('../' + itemId + (page ? '/' + page : ''));
  }

  navigateTo(path?: string) {
    this.router.navigate([path ?? `..`], { relativeTo: this.route });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
